import { useState } from 'react';
import { formatHours, type ReportDefinition, type ReportSnapshot } from '@forecastify/shared';
import { Button, DropdownMenu, Icon, Progress } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { ReportChartView } from './report-charts';

/**
 * ⚠️ **Per reader, not per report — `ResultTable` argues it.** One key for the whole product
 * rather than one per report id: somebody who hides the estimate column means it, and re-hiding it
 * on every report they open is the setting failing to be a setting.
 */
const COLUMNS_KEY = 'forecastify.report.hiddenColumns';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A report's result — the bar chart, the pie, and the table.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `markup.html` 1326–1400. Every number arrives already computed: the server owns the arithmetic,
 * both percentages and the total, so the chart and the table cannot disagree about what a figure
 * means (`hours-chart.tsx` records the same rule for the project tile).
 *
 * ⚠️ **The bars are scaled to the TALLEST and the pie to the TOTAL, and that is deliberate.** Bars
 * scaled to the total are all short and the shape is unreadable; a pie has to sum to the whole or it
 * is not a pie. `barPercent` and `sharePercent` are two different questions with two different
 * denominators, which is why the snapshot carries both.
 */

/**
 * A group's colour, or an identity hue by position.
 *
 * ⚠️⚠️ **THE SERVER CANNOT NAME A CSS TOKEN, SO NULL MEANS "PICK ONE".** Projects, clients and
 * statuses store a colour; members and metafield values do not, and the design system's `--id-1 …
 * --id-8` exist for exactly this — *"label projects, clients and people by identity, never by
 * rank"*. Position is stable because the snapshot is stored, so a colour does not move under
 * somebody between two reads of the same report.
 */
function colorOf(color: string | null, index: number): string {
  return color ?? `var(--id-${String((index % 8) + 1)})`;
}

/**
 * What to call a group whose dimension value is null.
 *
 * ⚠️⚠️ **THE SERVER SENDS NULL AND THE SCREEN WORDS IT, BECAUSE THE WORDING DEPENDS ON THE
 * DIMENSION.** *"Projekt nélkül"* and *"Belső"* are different sentences for the same absence — the
 * mockup calls a client-less project *"Belső"* and nothing else could — and rule 7 puts every
 * user-facing string on this side of the wire anyway.
 */
export function groupName(
  t: Translate,
  dimension: ReportDefinition['dimension'],
  name: string | null,
): string {
  /**
   * C60.1 · ⚠⚠ **RISK SENDS A MACHINE IDENTIFIER AND THE SCREEN WORDS IT.** The server never
   * sends a Hungarian string (rule 7), so the band arrives as `ok` / `watch` / `risky` /
   * `unscanned` and is translated here — through the SAME keys the pill and the projects filter
   * use, so a report and a list cannot word the same band differently.
   *
   * ⚠️ **`unscanned` is a real group, not a missing value.** A report that dropped the projects
   * nobody has scanned would make its total disagree with the project count beside it.
   */
  if (dimension === 'risk') {
    if (name === 'ok' || name === 'watch' || name === 'risky') return t(`risk.band.${name}`);
    return t('report.group.unscanned');
  }
  /* 26 Oct 2026 · the booking arrives as `hard` / `soft`, worded as the resource grid words it. */
  if (dimension === 'booking') {
    if (name === 'hard') return t('resource.cell.hard');
    if (name === 'soft') return t('resource.cell.soft');
  }
  if (name !== null) return name;
  if (dimension === 'project') return t('report.group.noProject');
  if (dimension === 'client') return t('report.group.internal');
  if (dimension === 'assignee') return t('report.group.unassigned');
  /* 17 Oct 2026 · a worklog's line item and contract: *none* is the unrouted hours. */
  if (dimension === 'lineitem') return t('report.group.noLineItem');
  if (dimension === 'contract') return t('report.group.noContract');
  return t('report.group.noValue');
}

export function ReportResult({
  definition,
  snapshot,
  showTable,
}: {
  readonly definition: ReportDefinition;
  readonly snapshot: ReportSnapshot;
  readonly showTable: boolean;
}) {
  const t = useT();
  const { rows } = snapshot;

  if (rows.length === 0) {
    return (
      <div className="bg-card text-card-foreground rounded-xl border shadow-sm">
        <div className="flex flex-col items-center gap-1.5 px-6 py-16 text-center">
          <div className="text-[13px] font-medium">{t('report.empty.title')}</div>
          <div className="text-muted-foreground text-[12.5px]">{t('report.empty.body')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
        {/**
         * ⚠️ **C59 · twenty-five shapes, one dispatcher.** The two hand-written charts that stood
         * here moved into `report-charts.tsx` as families — `vbar` and `donut` are their exact
         * counterparts, and `0136` renamed the stored values to match.
         */}
        <ReportChartView
          chart={definition.chart}
          definition={definition}
          snapshot={snapshot}
          labelOf={(row) => groupName(t, definition.dimension, row.name)}
        />
      </div>

      {showTable && <ResultTable definition={definition} snapshot={snapshot} />}

      {/**
       * ⚠️⚠️ **THE TRUNCATION, SAID OUT LOUD.** The chart shows the top eight; `groupCount` is how
       * many there were. The mockup slices to eight with nowhere to say so, and a report over
       * twenty-three projects then looks like a report over eight — which is the difference between
       * a summary and a wrong answer.
       */}
      {snapshot.groupCount > rows.length && (
        <p className="text-muted-foreground text-[11.5px]">
          {t('report.truncated', {
            shown: String(rows.length),
            total: String(snapshot.groupCount),
          })}
        </p>
      )}
      <p className="text-muted-foreground text-[11.5px]">{t('report.trimmedNote')}</p>
    </div>
  );
}

/** The three figures a row can carry. The dimension column is not optional — it is the row. */
const RESULT_COLUMNS = ['count', 'estimate', 'share'] as const;
type ResultColumn = (typeof RESULT_COLUMNS)[number];

/**
 * `markup.html` 1367–1397 — and the columns are choosable since item 56.
 *
 * ⚠️⚠️ **HOURS AND THE DIMENSION ARE NOT IN THE PICKER, AND THAT IS THE DESIGN.** The dimension
 * column IS the row, and hours is what every report in this product is ultimately about — a table
 * with neither is not a narrower report, it is an empty one. What the feedback asked for is
 * control over the three that are sometimes noise: the count, the estimate and the share bar.
 *
 * ⚠️⚠️ **THE CHOICE IS PER READER AND PER REPORT, IN `localStorage`.** It is not part of the
 * report DEFINITION: a definition is shared, saved and re-run, and one person hiding the estimate
 * column must not change what a colleague opens. Storage that fails - a private window, blocked
 * site data - simply shows every column, which is the state this table was in before.
 */
function ResultTable({
  definition,
  snapshot,
}: {
  readonly definition: ReportDefinition;
  readonly snapshot: ReportSnapshot;
}) {
  const t = useT();

  const [hidden, setHidden] = useState<readonly ResultColumn[]>(() => {
    try {
      const raw = globalThis.localStorage.getItem(COLUMNS_KEY);
      const parsed: unknown = raw === null ? [] : JSON.parse(raw);
      return Array.isArray(parsed)
        ? RESULT_COLUMNS.filter((column) => (parsed as unknown[]).includes(column))
        : [];
    } catch {
      return [];
    }
  });

  const toggle = (column: ResultColumn): void => {
    const next = hidden.includes(column)
      ? hidden.filter((one: ResultColumn) => one !== column)
      : [...hidden, column];
    setHidden(next);
    try {
      globalThis.localStorage.setItem(COLUMNS_KEY, JSON.stringify(next));
    } catch {
      /* A table that cannot remember is not a table that should fail. */
    }
  };

  const shows = (column: ResultColumn): boolean => !hidden.includes(column);

  const columnLabel = (column: ResultColumn): string => {
    /* One `t()` per branch with a literal key — a computed key is invisible to anything scanning. */
    if (column === 'count') {
      return definition.source === 'worklog'
        ? t('report.column.entries')
        : t('report.column.tasks');
    }
    if (column === 'estimate') return t('report.column.estimate');
    return t('report.column.share');
  };

  return (
    <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
      <div className="flex items-center gap-2 border-b px-4 py-2">
        <span className="flex-1 text-[13px] font-medium">{t('report.table')}</span>
        {/* ⚠️ The same control the lists carry, and the same words - a reader who has met
         *Oszlopok* on the task list should not have to learn a second name for it here. */}
        <DropdownMenu
          label={t('list.columns')}
          align="end"
          width={220}
          items={RESULT_COLUMNS.map((column) => ({
            label: columnLabel(column),
            checked: shows(column),
            onSelect: () => {
              toggle(column);
            },
          }))}
        >
          <Button variant="outline" size="sm" className="gap-1.5">
            <Icon name="sliders-horizontal" size={14} />
            {t('list.columns')}
          </Button>
        </DropdownMenu>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <caption className="sr-only">{t('report.table')}</caption>
          <thead>
            <tr className="border-b">
              <th scope="col" className="text-muted-foreground px-4 py-2 text-left font-medium">
                {dimensionLabel(t, definition)}
              </th>
              <th
                scope="col"
                className="text-muted-foreground w-[130px] px-4 py-2 text-right font-medium"
              >
                {t('report.column.hours')}
              </th>
              {/* ⚠️ *"Bejegyzés"* on a worklog report, *"Feladat"* on a task one — the mockup's
                  `repCountLabel`. The same column counts different things. */}
              {shows('count') && (
                <th
                  scope="col"
                  className="text-muted-foreground w-[120px] px-4 py-2 text-right font-medium"
                >
                  {columnLabel('count')}
                </th>
              )}
              {shows('estimate') && (
                <th
                  scope="col"
                  className="text-muted-foreground w-[120px] px-4 py-2 text-right font-medium"
                >
                  {t('report.column.estimate')}
                </th>
              )}
              {shows('share') && (
                <th
                  scope="col"
                  className="text-muted-foreground w-[170px] px-4 py-2 text-left font-medium"
                >
                  {t('report.column.share')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {snapshot.rows.map((row, index) => (
              <tr key={row.key} className="border-b last:border-0">
                <th scope="row" className="px-4 py-2 text-left font-medium">
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="size-2 shrink-0 rounded-[3px]"
                      style={{ background: colorOf(row.color, index) }}
                    />
                    {groupName(t, definition.dimension, row.name)}
                  </span>
                </th>
                <td className="px-4 py-2 text-right tabular-nums">{formatHours(row.hours)}</td>
                {shows('count') && (
                  <td className="px-4 py-2 text-right tabular-nums">{row.count}</td>
                )}
                {/* ⚠️⚠️ **AN EM-DASH, NEVER A ZERO.** A worklog has no estimate at all, so the
                    server sends null — and `0:00` would claim somebody estimated nothing. The kit's
                    rule for an uncomputable figure. */}
                {shows('estimate') && (
                  <td className="text-muted-foreground px-4 py-2 text-right tabular-nums">
                    {row.estimate === null ? '–' : formatHours(row.estimate)}
                  </td>
                )}
                {shows('share') && (
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-2">
                      <Progress
                        value={row.barPercent}
                        color={colorOf(row.color, index)}
                        label={groupName(t, definition.dimension, row.name)}
                        className="w-[90px]"
                      />
                      <span className="text-muted-foreground text-[11.5px] tabular-nums">
                        {`${String(row.sharePercent)}%`}
                      </span>
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The dimension's name, for the table's first column and the builder's rail. */
export function dimensionLabel(t: Translate, definition: ReportDefinition): string {
  /* One `t()` per branch with a literal key — a computed key is invisible to anything scanning. */
  if (definition.dimension === 'project') return t('report.dim.project');
  if (definition.dimension === 'client') return t('report.dim.client');
  if (definition.dimension === 'assignee') return t('report.dim.assignee');
  if (definition.dimension === 'status') return t('report.dim.status');
  if (definition.dimension === 'role') return t('report.dim.role');
  /* C60 · the four that only mean something over a money source. */
  if (definition.dimension === 'contract') return t('report.dim.contract');
  if (definition.dimension === 'lineitem') return t('report.dim.lineitem');
  if (definition.dimension === 'scope') return t('report.dim.scope');
  if (definition.dimension === 'risk') return t('report.dim.risk');
  if (definition.dimension === 'occurrence') return t('report.dim.occurrence');
  /* 17 Oct 2026 · a worklog's task. */
  if (definition.dimension === 'task') return t('report.dim.task');
  /* 26 Oct 2026 · an allocation's hard / soft booking. */
  if (definition.dimension === 'booking') return t('resource.cell.booking');
  return t('report.dim.metafield');
}
